// On-screen joystick, adapted from the cazacorruptos pattern (zone / base /
// thumb, normalized −1..1 clamped to the base radius). Uses Pointer Events so it
// works with both touch and mouse. Read `.x` / `.y` each frame.
export interface Joystick {
  readonly x: number; // −1 (left) .. 1 (right)
  readonly y: number; // −1 (up) .. 1 (down)
  setVisible(v: boolean): void;
  dispose(): void;
}

export function createJoystick(parent: HTMLElement = document.body): Joystick {
  const BASE = 130;
  const THUMB = 54;
  const max = (BASE - THUMB) / 2;

  // Centred along the bottom on mobile portrait; bottom-left in landscape/desktop.
  const zone = document.createElement("div");
  zone.className =
    "fixed bottom-6 left-1/2 z-20 -translate-x-1/2 touch-none select-none portrait:bottom-20 landscape:left-6 landscape:translate-x-0";
  zone.style.width = `${BASE}px`;
  zone.style.height = `${BASE}px`;

  // Transient "how to drive" hint above the knob.
  const hint = document.createElement("div");
  hint.className =
    "pointer-events-none absolute -top-12 left-1/2 hidden -translate-x-1/2 whitespace-nowrap rounded-lg border-2 border-black bg-white/90 px-3 py-1 text-xs font-bold shadow";
  hint.textContent = "Arrastra: ←→ girar · ↕ avanzar";

  const base = document.createElement("div");
  base.className =
    "relative h-full w-full rounded-full border-2 border-black/40 bg-black/5";
  const thumb = document.createElement("div");
  thumb.className =
    "absolute left-1/2 top-1/2 rounded-full border-2 border-black bg-black/20";
  thumb.style.width = `${THUMB}px`;
  thumb.style.height = `${THUMB}px`;
  thumb.style.transform = "translate(-50%, -50%)";
  thumb.style.pointerEvents = "none";
  base.appendChild(thumb);
  zone.appendChild(base);
  zone.appendChild(hint);
  parent.appendChild(zone);

  const state = { x: 0, y: 0, id: -1 };

  // Show the hint until the player first grabs the joystick (or 6s elapse).
  let used = false;
  let hintTimer: number | undefined;
  const hideHint = () => {
    hint.classList.add("hidden");
    clearTimeout(hintTimer);
  };
  const showHint = () => {
    if (used) return;
    hint.classList.remove("hidden");
    clearTimeout(hintTimer);
    hintTimer = setTimeout(hideHint, 6000);
  };

  const moveTo = (clientX: number, clientY: number) => {
    const r = base.getBoundingClientRect();
    let dx = clientX - (r.left + r.width / 2);
    let dy = clientY - (r.top + r.height / 2);
    const d = Math.hypot(dx, dy);
    if (d > max) {
      dx = (dx / d) * max;
      dy = (dy / d) * max;
    }
    state.x = dx / max;
    state.y = dy / max;
    thumb.style.transform =
      `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
  };

  const reset = () => {
    state.x = 0;
    state.y = 0;
    state.id = -1;
    thumb.style.transform = "translate(-50%, -50%)";
  };

  const onDown = (e: PointerEvent) => {
    e.preventDefault();
    used = true;
    hideHint();
    state.id = e.pointerId;
    zone.setPointerCapture(e.pointerId);
    moveTo(e.clientX, e.clientY);
  };
  const onMove = (e: PointerEvent) => {
    if (e.pointerId === state.id) moveTo(e.clientX, e.clientY);
  };
  const onUp = (e: PointerEvent) => {
    if (e.pointerId === state.id) reset();
  };

  zone.addEventListener("pointerdown", onDown);
  zone.addEventListener("pointermove", onMove);
  zone.addEventListener("pointerup", onUp);
  zone.addEventListener("pointercancel", onUp);

  return {
    get x() {
      return state.x;
    },
    get y() {
      return state.y;
    },
    setVisible(v: boolean) {
      zone.style.display = v ? "block" : "none";
      if (v) showHint();
      else hideHint();
    },
    dispose() {
      clearTimeout(hintTimer);
      zone.removeEventListener("pointerdown", onDown);
      zone.removeEventListener("pointermove", onMove);
      zone.removeEventListener("pointerup", onUp);
      zone.removeEventListener("pointercancel", onUp);
      zone.remove();
    },
  };
}
